import { storeToRefs } from "pinia";
import { useBoardCanvasStore } from "./store/board";
import { useEditorStore } from "./store/editor";
import { objExt } from "@xmiles/holopix-helper";
import { logger } from "holopix-helper";
import { saveLayerList } from "holopix-api";
import { saveBoardSnapshot } from "./storage";
import { CANVAS_OBJECT_FIELDS } from "../../helper/canvasConfig";
import { compressCanvasData } from "./optimization/canvasDataCompress";
import { isImageOrVideoObject } from "./layerUtils";
import { restoreDowngradedType } from "./init/typeDowngrade";
import { splitTimeline, countSerializedSplitGroups, describeSerializedSplitGroups } from "./splitCanvasTimeline";
import type { Ref } from "vue";
import type { IEditor } from "board-core";

/**
 * 准备画板数据（用于保存）
 * 从画布中提取完整的画板数据，包括图层、视口状态和配置信息
 * 
 * 【优化说明】
 * 1. 使用 canvasDataCompress 模块压缩数据，剔除 Fabric.js 默认值
 * 2. 预期压缩率 60-70%，大幅减少上传数据量
 * 3. 保留压缩标记，便于后续版本兼容
 */
export const getBoardDataForSave = () => {
  const boardCanvasStore = useBoardCanvasStore();
  const editorStore = useEditorStore();
  const { editor: editorRef } = storeToRefs(editorStore) as unknown as { editor: Ref<IEditor | null> };

  const canvas = editorRef.value?.fabricCanvas;
  if (!canvas) return null;

  // 1. 获取当前视口信息
  const zoom = canvas.getZoom();
  const vpt = canvas.viewportTransform;
  const pan = { x: vpt?.[4] || 0, y: vpt?.[5] || 0 };

  const currentBoardConfig = {
    viewport: {
      zoom: zoom * 100, // 保存为百分比
      pan
    }
  };

  // 2. 获取画布数据并过滤
  // 序列化时包含自定义属性，确保数据完整性
  // 增加生图相关字段：generateParams, generationId, resultId
  // 增加 originalSrc 字段：保存图片原始URL，用于带水印下载
  const json = canvas.toJSON([...CANVAS_OBJECT_FIELDS]);

  // 过滤掉 workspace 和 临时图层，并精简对象数据
  const filteredObjects = (json.objects || []).reduce((acc: any[], obj: any) => {
    if (obj.temporaryLayerInfo) return acc;

    // 还原降级对象的原始类型（跨分支数据兼容）
    restoreDowngradedType(obj);

    // 精简图片/视频封面对象数据
    if (isImageOrVideoObject(obj)) {
      const imageObj = obj as any;
      // 移除样式属性（加载时会强制重置为默认值，无需保存）
      delete imageObj.borderColor;
      delete imageObj.cornerColor;
      delete imageObj.cornerSize;
      delete imageObj.transparentCorners;
      delete imageObj.cornerStrokeColor;
      delete imageObj.borderScaleFactor;
      delete imageObj.hasRotatingPoint;
      delete imageObj.lockRotation;
      delete imageObj.lockScalingFlip;

      // 移除空滤镜数组
      if (Array.isArray(imageObj.filters) && imageObj.filters.length === 0) {
        delete imageObj.filters;
      }
      delete imageObj.resizeFilters;
    }

    acc.push(obj);
    return acc;
  }, []);

  // 【临时诊断】打印保存对象结构，定位拆分图组展平问题；[Split50] 附加图组 tag 确认持久化
  if (import.meta.env.DEV) {
    console.log(
      '[save] objects:',
      filteredObjects.map((o: any) => ({
        type: o.type,
        id: o.id,
        name: o.name,
        childCount: (o.objects || []).length,
        // [Split50] 拆分图组关键信息是否随保存带上
        ...(o._isSplitGroup || o._splitTaskId || o._splitClientId
          ? { split: { t: o._splitTaskId, c: o._splitClientId, r: o._splitResultRevision, childFiles: (o.objects || []).map((c: any) => c._splitLayerFile) } }
          : {}),
      })),
    );
  }

  // layerData 包含视口参数
  const layerData = {
    ...json,
    viewport: currentBoardConfig.viewport,
    id: boardCanvasStore.boardId,
    objects: filteredObjects
  };

  // 3. 【新增】压缩画布数据，剔除默认值
  const compressedLayerData = compressCanvasData(layerData);

  return { layerData: compressedLayerData };
};

/**
 * 仅保存到本地缓存（无阻塞，快速）
 * 适用于高频自动保存场景，如用户操作后的快速备份
 */
export const saveBoardLocal = async () => {
  if (!isSaveEnabled) return;

  const saveEpoch = localSaveEpoch;
  const boardCanvasStore = useBoardCanvasStore();
  const data = getBoardDataForSave();
  if (!data) return;

  const { layerData } = data;
  const boardId = boardCanvasStore.boardId;

  try {
    // 使用 JSON 序列化/反序列化来深拷贝数据
    // 目的：
    // 1. 去除 Vue 的 Proxy 包装（导致 DataCloneError 主要原因）
    // 2. 移除 undefined、Function 等无法存储的类型
    // 3. 确保数据是纯净的 JSON 对象
    const snapshot = JSON.parse(
      JSON.stringify({
        boardId,
        canvasData: layerData
      })
    );

    if (!isSaveEnabled || saveEpoch !== localSaveEpoch) return;

    await saveBoardSnapshot(Number(boardId), snapshot);
    // 诊断（静默入时间线）：本地快照的拆分图组数量与各图组 children 数——children 归零/丢层发生在哪次保存前后以此对账
    splitTimeline(
      "save-local-ok",
      {
        boardId,
        splitGroups: countSerializedSplitGroups(layerData?.objects),
        groups: describeSerializedSplitGroups(layerData?.objects),
      },
      { silent: true },
    );
  } catch (err) {
    splitTimeline("save-local-fail", { error: String(err) });
    console.warn("[Board] Failed to save local snapshot:", err);
  }
};

/**
 * 保存队列状态管理
 * 用于防止并发保存请求导致的竞态条件
 */
let isSaving = false;
let localSaveEpoch = 0;
let pendingSaveCount = 0; // 使用计数器记录挂起的保存请求数量
let isSaveEnabled = true; // 控制是否允许保存（用于组件销毁后阻止保存）

/**
 * 设置是否允许保存
 * @param enabled 是否启用
 */
export const setSaveEnabled = (enabled: boolean) => {
  isSaveEnabled = enabled;
  if (!enabled) {
    localSaveEpoch++;
    pendingSaveCount = 0; // 禁用时清除挂起的保存
  }
};

/**
 * 获取当前保存状态
 * @returns 是否正在保存
 */
export const getIsSaving = () => isSaving;

/**
 * 获取挂起的保存请求数量
 * @returns 挂起的保存请求数
 */
export const getPendingSaveCount = () => pendingSaveCount;

/**
 * 获取是否有挂起的保存请求（向后兼容）
 * @returns 是否有待处理的保存
 */
export const getHasPendingSave = () => pendingSaveCount > 0;

/**
 * 重置保存队列状态
 * 用于异常恢复或测试场景
 */
export const resetSaveState = () => {
  isSaving = false;
  localSaveEpoch = 0;
  pendingSaveCount = 0;
  isSaveEnabled = true;
};

/**
 * 保存画板数据到服务器（包含重试机制和队列管理）
 * @param _force 是否强制保存（保留参数以兼容未来扩展）
 */
export const saveBoard = async (_force = false) => {
  // 如果保存已禁用且非强制保存，直接返回
  if (!isSaveEnabled && !_force) {
    console.log("[BoardSave] Save disabled, skipping");
    return;
  }

  const boardCanvasStore = useBoardCanvasStore();

  // 如果正在保存，累加挂起的保存请求计数器，确保不丢失任何变更
  if (isSaving) {
    pendingSaveCount++;
    console.log(`[BoardSave] Save in progress, pending count: ${pendingSaveCount}`);
    return;
  }

  const data = getBoardDataForSave();
  if (!data) return;
  const { layerData } = data;
  const boardId = boardCanvasStore.boardId;

  isSaving = true;
  const currentPendingCount = pendingSaveCount;
  pendingSaveCount = 0; // 重置计数器

  try {
    await saveLayerList({ boardId, content: objExt.stringify(layerData) });

    logger.info("画板数据保存成功", { boardId });
    // 诊断（静默入时间线）：服务端保存成功时的拆分图组数量与 children 明细（「保存静默全灭」/「空组入库」取证）
    splitTimeline(
      "save-server-ok",
      {
        boardId,
        splitGroups: countSerializedSplitGroups(layerData?.objects),
        groups: describeSerializedSplitGroups(layerData?.objects),
      },
      { silent: true },
    );
  } catch (error) {
    splitTimeline("save-server-fail", { error: String(error) });
    console.error("画板数据保存失败", error);
  } finally {
    isSaving = false;
    // 如果保存功能启用，且有挂起的变更，立即触发下一次保存
    if (isSaveEnabled && (pendingSaveCount > 0 || currentPendingCount > 0)) {
      console.log(`[BoardSave] Processing pending saves: ${pendingSaveCount + currentPendingCount}`);
      // 使用 setTimeout 避免递归栈溢出，并让出主线程
      setTimeout(() => saveBoard(), 100);
    }
  }
};
